import React, { useMemo, useState } from 'react';
import { Button, SubHeader, Icon, Alert, Table } from 'vetrf-ui';
import { RemoveModal } from 'components/RemoveModal';
import { BackendFileType } from 'api/file/params';
import { RegistryProductFile, FileType } from 'types/types';
import { fileApi } from 'api/file';
import { UploadFormModal } from './components/UploadFormModal';
import { getColumns } from './consts';

type RegistryProductFilesProps = {
  entryId: string;
  list: RegistryProductFile[] | undefined;
  removeMethod: (entryId: string, itemId: string) => Promise<void>;
  updateMethod: (entryId: string, data: RegistryProductFile) => Promise<void>;
  updateCard: () => void;
  fileType: BackendFileType;
  editable: boolean;
  title: string;
  modalTitle: string;
  listEmptyText: string;
};

export const RegistryProductFiles = ({
  entryId,
  list,
  updateMethod,
  removeMethod,
  updateCard,
  fileType,
  editable,
  title,
  modalTitle,
  listEmptyText,
}: RegistryProductFilesProps) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [removeItem, setRemoveItem] = useState<RegistryProductFile>();
  const [currentItem, setCurrentItem] = useState<RegistryProductFile>();
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  const closeAddModal = () => {
    setCurrentItem(undefined);
    setIsAddModalOpen(false);
  };

  const openAddModal = (item?: RegistryProductFile) => {
    setCurrentItem(item);
    setIsAddModalOpen(true);
  };

  const openRemoveModal = (item: RegistryProductFile) => {
    setRemoveItem(item);
    setIsRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    setRemoveItem(undefined);
    setIsRemoveModalOpen(false);
  };

  const saveItem = (data: RegistryProductFile) => {
    updateMethod(entryId, data)
      .then(updateCard)
      .catch(() => setError(true))
      .finally(() => {
        closeAddModal();
        setSaving(false);
      });
  };

  const onSubmit = (data: RegistryProductFile, selectedFile: File) => {
    setError(false);
    setSaving(true);
    if (selectedFile) {
      fileApi
        .uploadFile(selectedFile, fileType)
        .then((file: FileType) => saveItem({ ...data, file }))
        .catch(() => {
          setError(true);
          setSaving(false);
        });
    } else {
      saveItem(data);
    }
  };

  const onRemove = async () => {
    setError(false);
    removeMethod(entryId, removeItem!.id!)
      .then(updateCard)
      .catch(() => setError(true))
      .finally(closeRemoveModal);
  };

  const columns = useMemo(() => {
    const cols = getColumns();
    if (editable) {
      cols.push({
        dataFormatter: (item) => (
          <>
            <Button
              icon="pencil"
              iconColor="green"
              iconSize={150}
              onClick={() => openAddModal(item as RegistryProductFile)}
              onlyIcon
              tooltip="Редактировать"
            />
            <Button
              icon="trash"
              iconColor="red"
              iconSize={150}
              onClick={() => openRemoveModal(item as RegistryProductFile)}
              onlyIcon
              tooltip="Удалить"
            />
          </>
        ),
        className: 'actions',
        key: 'actions',
        title: '',
        width: '1%',
      });
    }
    return cols;
  }, [editable]);

  return (
    <>
      <SubHeader
        header={title}
        icon="book"
        iconColor="orange"
        iconSize={100}
        level={4}
        toolbar={
          editable && (
            <Button
              color="info"
              size="minier"
              text="Добавить"
              icon="plus"
              onClick={() => openAddModal()}
            />
          )
        }
      />
      <div>
        {error && (
          <Alert closable messageType="danger">
            Ошибка при обновлении
          </Alert>
        )}

        {list?.length ? (
          <div className="table-container">
            <Table columns={columns} dataList={list} />
          </div>
        ) : (
          <Alert messageType="info" style={{ display: 'flex', alignItems: 'center' }}>
            <Icon color="blue" icon="frown-o" size={120} />
            <span style={{ marginLeft: '8px' }}>{listEmptyText}</span>
          </Alert>
        )}

        {isAddModalOpen && (
          <UploadFormModal
            defaultValues={currentItem}
            onClose={closeAddModal}
            onSubmit={onSubmit}
            saving={saving}
            title={`${currentItem ? 'Редактирование' : 'Добавление'} ${modalTitle}`}
          />
        )}

        {isRemoveModalOpen && (
          <RemoveModal
            onClose={closeRemoveModal}
            onRemove={onRemove}
            modalContent={`Вы уверены, что хотите удалить ${removeItem!.name}?`}
          />
        )}
      </div>
    </>
  );
};
